import classNames from "classnames";
import { Ref } from "react";

interface ButtonProps {
  type: "button" | "submit" | "reset";
  children: React.ReactNode;
  size?: "small" | "medium" | "large";
  ariaLabel?: string;
  variant?: "solid" | "outline";
  buttonClass?: string;
  disabled?: boolean;
  ariaDisabled?: boolean;
  isLoading?: boolean;
  contained?: boolean;
  onClick?: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
}

export function Button({
  type,
  children,
  ariaLabel = "",
  variant = "solid",
  disabled = false,
  ariaDisabled,
  isLoading = false,
  contained = false,
  buttonClass = "",
  size = "medium",
  onClick = () => {},
  buttonRef,
}: ButtonProps) {
  const sizeClasses = {
    small: "relative hit-target min-w-8 min-h-8 py-1 px-2 text-sm",
    medium: "min-w-target min-h-target py-2 px-4 text-base",
    large: "min-w-12 min-h-12 py-3 px-6 text-lg",
  };

  return (
    <button
      ref={buttonRef}
      type={type}
      disabled={disabled}
      aria-disabled={ariaDisabled || undefined}
      aria-busy={isLoading}
      className={classNames(
        "focus:outline-none focus:ring-2",
        "rounded capitalize font-comfortaa font-medium transition-colors duration-200",
        "flex items-center justify-center",
        sizeClasses[size],
        {
          "cursor-not-allowed": disabled || ariaDisabled,
          "aria-disabled:bg-opacity-40 aria-disabled:hover:bg-primary":
            ariaDisabled && variant === "solid",
          "aria-disabled:text-opacity-40 aria-disabled:hover:bg-transparent":
            ariaDisabled && variant === "outline",
          "w-full": !contained,
          "w-fit px-2": contained,
          "bg-primary text-on-primary disabled:bg-opacity-40 hover:bg-primary-hover disabled:hover:bg-primary":
            variant === "solid",
          "bg-transparent border border-primary text-primary disabled:text-opacity-40 hover:bg-tint disabled:hover:bg-transparent":
            variant === "outline",
          "cursor-wait": isLoading,
        },
        buttonClass,
      )}
      onClick={ariaDisabled ? undefined : onClick}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

export default Button;
